// frontend/src/telas/pdf/PdfCartogramaPrint.js

import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { EscalaGrafica } from "./EscalaGrafica";
import {
  ContainerTextoRodapeH,
  FolhaA4,
  PDFContainer,
  RodapePdf,
  SpanTitulo,
  SpanTitulo2,
  TextoInfo,
  TextoRodapePdf,
} from "./pdf-cartograma-estilo";
import { MapaGraticule } from "../../componentes/mapa-leaflet/MapaGraticule";
import { getDadosImovel } from "../../services/api/fusio";
import { BASE_URL_GEO } from "../../services/common";
import ImgLogoEmbrapa from "../../assets/logo-embrapa.png";
import ImgZoom from "../../assets/zoom.png";
import {
  ContainerTitulo,
  Header,
  LogoEmbrapa,
  SpanNomeMunicipioFazenda,
  SpanNumeroCar,
} from "./pdf-serie-historia-estilo";

import ReactToPrint from "react-to-print";
import { Button } from "@mui/material";
import { decrementLoad, incrementLoad } from "../../redux/reducer";

// Leaflet (mini-mapa satélite do topo)
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";

// ============================ CONFIG GEOSERVER ============================
const GEOSERVER_DEFAULT_URL = "https://localhost/geoserver";
const GEOSERVER_BASE = BASE_URL_GEO ?? GEOSERVER_DEFAULT_URL;

const LAYER_USO_2008 = "lair:2008";
const LAYER_USO_2011 = "lair:2011";
const LAYER_USO_2016 = "lair:2016";
const LAYER_USO_2021 = "lair:2021";
const LAYER_DECLIVIDADE = "lair:declividade";
const LAYER_SOLO = "lair:aptidao_edafo";
const LAYER_MUNICIPIO = "lair:municipio";

const STYLE_USO_2008 = "";
const STYLE_USO_2011 = "";
const STYLE_USO_2016 = "";
const STYLE_USO_2021 = "uso_abc";
const STYLE_DECLIVIDADE = "";
const STYLE_SOLO = "";
const STYLE_MUNICIPIO = "municipio";

const IMG_W = 240;
const IMG_H = 240;
// ========================================================================

// -------- helpers geom/bbox --------
function getBoundsFromGeoJSON(geom) {
  try {
    if (!geom || !geom.type || !geom.coordinates) return null;
    let coords = [];
    if (geom.type === "Polygon") coords = geom.coordinates.flat();
    else if (geom.type === "MultiPolygon") coords = geom.coordinates.flat(2);
    else return null;
    const lats = coords.map((c) => c[1]);
    const lngs = coords.map((c) => c[0]);
    return [
      [Math.min(...lats), Math.min(...lngs)],
      [Math.max(...lats), Math.max(...lngs)],
    ];
  } catch {
    return null;
  }
}

function padBbox(bbox, factor = 0.05) {
  if (!bbox) return null;
  const [[ymin, xmin], [ymax, xmax]] = bbox;
  const dx = (xmax - xmin) * factor;
  const dy = (ymax - ymin) * factor;
  return [
    [ymin - dy, xmin - dx],
    [ymax + dy, xmax + dx],
  ];
}

function expandBboxToMinSize(bbox, minSize = 0.05) {
  if (!bbox) return null;
  const [[ymin, xmin], [ymax, xmax]] = bbox;
  const width = xmax - xmin;
  const height = ymax - ymin;
  const padX = Math.max(0, (minSize - width) / 2);
  const padY = Math.max(0, (minSize - height) / 2);
  return [
    [ymin - padY, xmin - padX],
    [ymax + padY, xmax + padX],
  ];
}

function bboxToParams(bbox) {
  const [[ymin, xmin], [ymax, xmax]] = bbox;
  return `${xmin},${ymin},${xmax},${ymax}`;
}

function bboxToParamsLatLon(bbox) {
  const [[ymin, xmin], [ymax, xmax]] = bbox;
  return `${ymin},${xmin},${ymax},${xmax}`;
}

// -------- helpers WMS --------
function wmsPngUrl({
  layer,
  style = "",
  bbox,
  width = IMG_W,
  height = IMG_H,
  cql = "",
  srs = "EPSG:4674",
}) {
  const params = new URLSearchParams({
    service: "WMS",
    request: "GetMap",
    version: "1.1.0",
    layers: layer,
    styles: style,
    format: "image/png",
    transparent: "true",
    srs,
    width: String(width),
    height: String(height),
    bbox: bboxToParams(bbox),
  });
  if (cql) params.set("cql_filter", cql);
  return `${GEOSERVER_BASE}/wms?${params.toString()}`;
}

function legendUrl({ layer, style = "", scale = 250000, w = 220, h = 220 }) {
  const params = new URLSearchParams({
    request: "GetLegendGraphic",
    service: "WMS",
    format: "image/png",
    width: String(w),
    height: String(h),
    layer,
    ...(style ? { style } : {}),
    SCALE: String(scale),
    LEGEND_OPTIONS:
      "forceLabels:on;fontAntiAliasing:true;fontColor:0x000000;fontSize:12;dpi:180",
    FORMAT_OPTIONS: "dpi:180",
    transparent: "true",
  });
  return `${GEOSERVER_BASE}/wms?${params.toString()}`;
}

// ===================== Legenda HTML – Uso do Solo ======================
function LegendUsoSoloHtml() {
  const items = [
    { label: "Pastagem", color: "#BDB70F" },
    { label: "Agricultura", color: "#FFE500" },
    { label: "Agropecuária", color: "#CFA29C" },
    { label: "Silvicultura", color: "#6AA84F" },
    { label: "Natural não vegetada", color: "#9E9E9E" },
    { label: "Vegetação florestal", color: "#7F9E23" },
    { label: "Vegetação não florestal", color: "#B7E28B" },
    { label: "Influência urbana", color: "#FFB3C6" },
    { label: "Água", color: "#9CCAD3" },
  ];

  return (
    <div
      style={{
        width: 220,
        background: "white",
        borderRadius: 8,
        boxShadow: "0 1px 6px rgba(0,0,0,0.15)",
        border: "1px solid rgba(0,0,0,0.15)",
        padding: 12,
        fontSize: 12,
        lineHeight: 1.3,
      }}
    >
      <div style={{ textAlign: "center", fontWeight: 600, marginBottom: 8 }}>
        Uso do Solo
      </div>
      <div>
        {items.map((it) => (
          <div
            key={it.label}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              margin: "6px 0",
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: 14,
                height: 14,
                borderRadius: 2,
                border: "1px solid rgba(0,0,0,0.3)",
                background: it.color,
                flex: "0 0 auto",
              }}
            />
            <span style={{ color: "#000", whiteSpace: "nowrap" }}>
              {it.label}
            </span>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 10, color: "#444", textAlign: "center", marginTop: 6 }}>
        Parâmetros da legenda são fixos devido a limitações do GeoServer.
      </div>
    </div>
  );
}

function wmsPngUrlLatLon({
  layer,
  style = "",
  bbox,
  width = IMG_W,
  height = IMG_H,
  cql = "",
  srs = "EPSG:4674",
}) {
  const params = new URLSearchParams({
    service: "WMS",
    request: "GetMap",
    version: "1.1.0",
    layers: layer,
    styles: style,
    format: "image/png",
    transparent: "true",
    srs,
    width: String(width),
    height: String(height),
    bbox: bboxToParamsLatLon(bbox),
  });
  if (cql) params.set("cql_filter", cql);
  return `${GEOSERVER_BASE}/wms?${params.toString()}`;
}

function wmsPngUrl13LatLon({
  layer,
  style = "",
  bbox,
  width = IMG_W,
  height = IMG_H,
  cql = "",
  crs = "EPSG:4674",
}) {
  return wmsPngUrl13LatLonBase({
    baseUrl: `${GEOSERVER_BASE}/wms`,
    layer,
    style,
    bbox,
    width,
    height,
    cql,
    crs,
  });
}

function wmsPngUrl13LatLonBase({
  baseUrl,
  layer,
  style = "",
  bbox,
  width = IMG_W,
  height = IMG_H,
  cql = "",
  crs = "EPSG:4674",
}) {
  const params = new URLSearchParams({
    service: "WMS",
    request: "GetMap",
    version: "1.3.0",
    layers: layer,
    styles: style,
    format: "image/png",
    transparent: "true",
    crs,
    width: String(width),
    height: String(height),
    bbox: bboxToParamsLatLon(bbox),
  });
  if (cql) params.set("cql_filter", cql);
  return `${baseUrl}?${params.toString()}`;
}

// ===================== Legenda HTML – Declividade ======================
function LegendDeclividadeHtml() {
  const items = [
    { label: "0 a 12%", color: "#00CC2A" },      // verde
    { label: "12 a 18%", color: "#FFF200" },     // amarelo
    { label: "18 a 45%", color: "#FF9900" },     // laranja
    { label: "45 a 100%", color: "#FF3300" },    // vermelho-escuro
    { label: "Acima de 100%", color: "#FF0000" },// vermelho
  ];
  return (
    <div
      style={{
        width: 220,
        background: "white",
        borderRadius: 8,
        boxShadow: "0 1px 6px rgba(0,0,0,0.15)",
        border: "1px solid rgba(0,0,0,0.15)",
        padding: 12,
        fontSize: 12,
        lineHeight: 1.3,
      }}
    >
      <div style={{ textAlign: "center", fontWeight: 700, marginBottom: 8 }}>
        declividade
      </div>
      {items.map((it) => (
        <div
          key={it.label}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            margin: "6px 0",
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: 14,
              height: 14,
              borderRadius: 2,
              border: "1px solid rgba(0,0,0,0.3)",
              background: it.color,
              flex: "0 0 auto",
            }}
          />
          <span style={{ color: "#000" }}>{it.label}</span>
        </div>
      ))}

      {/* Sem dados com hachura */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          margin: "6px 0",
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 14,
            height: 14,
            borderRadius: 2,
            border: "1px solid rgba(0,0,0,0.3)",
            background:
              "repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(0,0,0,0.35) 3px, rgba(0,0,0,0.35) 5px)",
            flex: "0 0 auto",
          }}
        />
        <span style={{ color: "#000" }}>Sem dados</span>
      </div>
      <div style={{ fontSize: 10, color: "#444", textAlign: "center", marginTop: 6 }}>
        Parâmetros da legenda são fixos devido a limitações do GeoServer.
      </div>
    </div>
  );
}

// =========== MiniMapaSat: polígono sobre imagem de satélite ===========
function MiniMapaSat({ geom, bbox, width = 160, height = 160 }) {
  const mapRef = useRef(null);

  const centerFromBbox = (b) => {
    const [[latMin, lngMin], [latMax, lngMax]] = b;
    return [(latMin + latMax) / 2, (lngMin + lngMax) / 2];
    // lat, lng (EPSG:4674)
  };

  if (!geom || !bbox) {
    return (
      <div
        style={{
          width,
          height,
          background: "#f3f3f3",
          border: "1px solid #e0e0e0",
          borderRadius: 4,
        }}
      />
    );
  }

  const center = centerFromBbox(bbox);

  return (
    <div style={{ width, height, borderRadius: 4, overflow: "hidden" }}>
      <MapContainer
        center={center}
        zoom={14}
        bounds={bbox}
        whenCreated={(m) => (mapRef.current = m)}
        whenReady={(e) => {
          const m = e.target;
          try {
            m.fitBounds(bbox, { padding: [5, 5] });
          } catch {}
          m.invalidateSize(true);
          setTimeout(() => m.invalidateSize(true), 200);
          setTimeout(() => m.invalidateSize(true), 600);
        }}
        style={{ width: "100%", height: "100%", background: "#eaeaea", pointerEvents: "none" }}
        zoomControl={false}
        attributionControl={false}
        dragging={false}
        doubleClickZoom={false}
        scrollWheelZoom={false}
        boxZoom={false}
        keyboard={false}
        touchZoom={false}
        preferCanvas={true}
      >
        <TileLayer
          url="https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          tileSize={512}
          zoomOffset={-1}
          maxNativeZoom={19}
          updateWhenIdle={true}
          updateWhenZooming={false}
          keepBuffer={1}
          crossOrigin="anonymous"
        />
        <GeoJSON
          data={geom}
          style={() => ({
            color: "#00bcd4",
            weight: 3,
            opacity: 1,
            fillColor: "#00bcd4",
            fillOpacity: 0.18,
          })}
        />
      </MapContainer>
    </div>
  );
}
// ========================================================================

export default function PdfCartogramaPrint() {
  console.warn("[PdfCartogramaPrint] render");
  const [bbox, setBbox] = useState(null);
  const [bboxMunicipio, setBboxMunicipio] = useState(null);
  const [bboxEscala, setBboxEscala] = useState(null);
  const [objetoPdf, setObjetoPdf] = useState({});
  const [geomImovel, setGeomImovel] = useState(null);
  const { numeroCar } = useParams();
  const dispatch = useDispatch();
  const componentRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!numeroCar) return;
      console.warn("[PdfCartogramaPrint] Iniciando carga para CAR:", numeroCar);
      dispatch(incrementLoad());
      try {
        const { data } = await getDadosImovel(numeroCar);
        const imovel = Array.isArray(data) ? data[0] : data;
        console.warn("[PdfCartogramaPrint] dados imovel:", imovel);

        let b = getBoundsFromGeoJSON(imovel?.geom);
        const normalizeBbox = (value) => {
          if (!Array.isArray(value) || value.length !== 2) return null;
          const a = value.map((p) =>
            Array.isArray(p) && p.length === 2
              ? [Number(p[0]), Number(p[1])]
              : [NaN, NaN]
          );
          if (!a.every((p) => p.every((n) => Number.isFinite(n)))) return null;
          return a;
        };
        b = normalizeBbox(b);
        if (!b) {
          const { st_xmin, st_ymin, st_xmax, st_ymax } = imovel || {};
          if (
            st_xmin != null &&
            st_ymin != null &&
            st_xmax != null &&
            st_ymax != null
          ) {
            b = normalizeBbox([
              [parseFloat(st_ymin), parseFloat(st_xmin)],
              [parseFloat(st_ymax), parseFloat(st_xmax)],
            ]);
          }
        }
        console.warn("[PdfCartogramaPrint] bbox bruto:", b, "raw:", JSON.stringify(b));
        if (!b) return;

        if (alive) {
          setBbox(b);
          setBboxEscala(b);
          setGeomImovel(imovel?.geom || null);
        }

        const bPad = padBbox(b, 0.07);
        const bPadMunicipio = expandBboxToMinSize(padBbox(b, 1.2), 0.12);
        console.warn(
          "[PdfCartogramaPrint] bbox com padding:",
          bPad,
          "raw:",
          JSON.stringify(bPad)
        );
        console.warn(
          "[PdfCartogramaPrint] bbox municipio:",
          bPadMunicipio,
          "raw:",
          JSON.stringify(bPadMunicipio)
        );
        const cqlCar = `cod_imovel='${String(numeroCar).replace(/'/g, "''")}'`;
        const cqlSituacao = `situacao='${String(numeroCar).replace(/'/g, "''")}'`;        
        console.warn("[PdfCartogramaPrint] cqlCar:", cqlCar);
        console.warn("[PdfCartogramaPrint] cqlSituacao:", cqlSituacao);

        const novo = {
          titulo: "Mapa de Caracterização da Propriedade",
          estado: imovel?.sigla_uf ?? "",
          municipioFazenda: imovel?.nm_municipio ?? "",

          legendaUsoTerra: legendUrl({
            layer: LAYER_USO_2021,
            style: STYLE_USO_2021,
            w: 240,
            h: 360,
            scale: 250000,
          }),

          imagemUsoTerra2008: wmsPngUrl({
            layer: LAYER_USO_2008,
            style: STYLE_USO_2008,
            bbox: bPad,
            cql: cqlCar,
            width: IMG_W,
            height: IMG_H,
          }),
          imagemUsoTerra2011: wmsPngUrl({
            layer: LAYER_USO_2011,
            style: STYLE_USO_2011,
            bbox: bPad,
            cql: cqlCar,
            width: IMG_W,
            height: IMG_H,
          }),
          imagemUsoTerra2016: wmsPngUrl({
            layer: LAYER_USO_2016,
            style: STYLE_USO_2016,
            bbox: bPad,
            cql: cqlCar,
            width: IMG_W,
            height: IMG_H,
          }),
          imagemUsoTerra2021: wmsPngUrl({
            layer: LAYER_USO_2021,
            style: STYLE_USO_2021,
            bbox: bPad,
            cql: cqlCar,
            width: IMG_W,
            height: IMG_H,
          }),

          // mantemos GetMap da camada (mapa grande), mas a legenda será ESTÁTICA
          imagemDeclividade: wmsPngUrl({
            layer: LAYER_DECLIVIDADE,
            style: STYLE_DECLIVIDADE,
            bbox: bPad,
            cql: cqlCar,
            width: 250,
            height: 250,
          }),

          legendasolo: legendUrl({
            layer: LAYER_SOLO,
            style: STYLE_SOLO,
            w: 220,
            h: 220,
            scale: 250000,
          }),
          imagemSolo: wmsPngUrl({
            layer: LAYER_SOLO,
            style: STYLE_SOLO,
            bbox: bPad,
            cql: cqlSituacao,
            width: 250,
            height: 250,
          }),
          imagemMunicipio: wmsPngUrl({
            layer: LAYER_MUNICIPIO,
            style: STYLE_MUNICIPIO,
            bbox: bPadMunicipio || bPad,
            width: 320,
            height: 180,
          }),
          imagemMunicipioSemStyle: wmsPngUrl({
            layer: LAYER_MUNICIPIO,
            style: "",
            bbox: bPadMunicipio || bPad,
            width: 320,
            height: 180,
          }),
          imagemMunicipio13: wmsPngUrl13LatLon({
            layer: LAYER_MUNICIPIO,
            style: STYLE_MUNICIPIO,
            bbox: bPadMunicipio || bPad,
            width: 320,
            height: 180,
          }),
          imagemMunicipio13SemStyle: wmsPngUrl13LatLon({
            layer: LAYER_MUNICIPIO,
            style: "",
            bbox: bPadMunicipio || bPad,
            width: 320,
            height: 180,
          }),
          imagemMunicipio13Local: wmsPngUrl13LatLonBase({
            baseUrl: `${GEOSERVER_BASE}/lair/wms`,
            layer: "municipio",
            style: STYLE_MUNICIPIO,
            bbox: bPadMunicipio || bPad,
            width: 320,
            height: 180,
          }),
          imagemMunicipio13LocalSemStyle: wmsPngUrl13LatLonBase({
            baseUrl: `${GEOSERVER_BASE}/lair/wms`,
            layer: "municipio",
            style: "",
            bbox: bPadMunicipio || bPad,
            width: 320,
            height: 180,
          }),
          imagemMunicipioLatLon: wmsPngUrlLatLon({
            layer: LAYER_MUNICIPIO,
            style: STYLE_MUNICIPIO,
            bbox: bPadMunicipio || bPad,
            width: 320,
            height: 180,
          }),
          imagemMunicipioLatLonSemStyle: wmsPngUrlLatLon({
            layer: LAYER_MUNICIPIO,
            style: "",
            bbox: bPadMunicipio || bPad,
            width: 320,
            height: 180,
          }),
        };

        console.warn("[PdfCartogramaPrint] URL municipio:", novo.imagemMunicipio);
        console.warn("[PdfCartogramaPrint] URL municipio sem style:", novo.imagemMunicipioSemStyle);
        console.warn("[PdfCartogramaPrint] URL municipio 1.3.0:", novo.imagemMunicipio13);
        console.warn("[PdfCartogramaPrint] URL municipio 1.3.0 sem style:", novo.imagemMunicipio13SemStyle);
        console.warn("[PdfCartogramaPrint] URL municipio 1.3.0 local ws:", novo.imagemMunicipio13Local);
        console.warn("[PdfCartogramaPrint] URL municipio 1.3.0 local ws sem style:", novo.imagemMunicipio13LocalSemStyle);
        console.warn("[PdfCartogramaPrint] URL municipio latlon:", novo.imagemMunicipioLatLon);
        console.warn("[PdfCartogramaPrint] URL municipio latlon sem style:", novo.imagemMunicipioLatLonSemStyle);
        if (alive) {
          setObjetoPdf(novo);
          setBboxMunicipio(bPadMunicipio || bPad);
        }
      } finally {
        dispatch(decrementLoad());
      }
    })();
    return () => {
      alive = false;
    };
  }, [numeroCar, dispatch]);

  const HeaderPdf = ({ titulo }) => (
    <Header>
      <LogoEmbrapa src={ImgLogoEmbrapa} />
      <ContainerTitulo>
        <SpanTitulo>{titulo ? titulo : (objetoPdf.titulo || "")}</SpanTitulo>
        <SpanNumeroCar>{`CAR ${numeroCar}`}</SpanNumeroCar>
        <SpanNomeMunicipioFazenda>
          {`${objetoPdf?.municipioFazenda || ""} - ${objetoPdf?.estado || ""}`}
        </SpanNomeMunicipioFazenda>
      </ContainerTitulo>
    </Header>
  );

  // Linha superior da página 1
  const LinhaTopoUso = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        minHeight: 180,
        gap: 24,
      }}
    >
      {/* Esquerda: mini satélite + “zoom” + graticule */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          width: 500,
          maxWidth: 500,
        }}
      >
        <MiniMapaSat geom={geomImovel} bbox={bbox} width={160} height={160} />
        <img src={ImgZoom} alt="ZoomImovel" width={70} height={180} />
        <div
          style={{
            width: 320,
            height: 180,
            display: "flex",
            alignItems: "stretch",
            overflow: "hidden",
            borderRadius: 4,
            position: "relative",
          }}
        >
          {objetoPdf?.imagemMunicipio ? (
            <img
              src={objetoPdf.imagemMunicipio || objetoPdf.imagemMunicipio13}
              alt="Municipio"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                zIndex: 1,
              }}
              onLoad={(e) => {
                const img = e.currentTarget;
                console.warn(
                  "[PdfCartogramaPrint] municipio carregou:",
                  img.src,
                  "size:",
                  img.naturalWidth,
                  "x",
                  img.naturalHeight
                );
              }}
              data-style-fallback="0"
              onError={(e) => {
                const img = e.currentTarget;
                const etapa = img.getAttribute("data-style-fallback") || "0";
                console.warn("[PdfCartogramaPrint] erro img municipio. etapa:", etapa, "src:", img.src);
                if (etapa === "0" && objetoPdf?.imagemMunicipioSemStyle) {
                  console.warn(
                    "[PdfCartogramaPrint] Fallback municipio: 1.1.0 sem style",
                    objetoPdf.imagemMunicipioSemStyle
                  );
                  img.setAttribute("data-style-fallback", "1");
                  img.src = objetoPdf.imagemMunicipioSemStyle;
                  return;
                }
                if (etapa === "1" && objetoPdf?.imagemMunicipio13Local) {
                  console.warn(
                    "[PdfCartogramaPrint] Fallback municipio: 1.3.0 local workspace",
                    objetoPdf.imagemMunicipio13Local
                  );
                  img.setAttribute("data-style-fallback", "2");
                  img.src = objetoPdf.imagemMunicipio13Local;
                  return;
                }
                if (etapa === "2" && objetoPdf?.imagemMunicipio13LocalSemStyle) {
                  console.warn(
                    "[PdfCartogramaPrint] Fallback municipio: 1.3.0 local ws sem style",
                    objetoPdf.imagemMunicipio13LocalSemStyle
                  );
                  img.setAttribute("data-style-fallback", "3");
                  img.src = objetoPdf.imagemMunicipio13LocalSemStyle;
                  return;
                }
                if (etapa === "3" && objetoPdf?.imagemMunicipio13SemStyle) {
                  console.warn(
                    "[PdfCartogramaPrint] Fallback municipio: 1.3.0 sem style",
                    objetoPdf.imagemMunicipio13SemStyle
                  );
                  img.setAttribute("data-style-fallback", "4");
                  img.src = objetoPdf.imagemMunicipio13SemStyle;
                  return;
                }
                if (etapa === "4" && objetoPdf?.imagemMunicipio13) {
                  console.warn(
                    "[PdfCartogramaPrint] Fallback municipio: 1.3.0 (global)",
                    objetoPdf.imagemMunicipio13
                  );
                  img.setAttribute("data-style-fallback", "5");
                  img.src = objetoPdf.imagemMunicipio13;
                  return;
                }
                console.warn("[PdfCartogramaPrint] Mapa municipio falhou em todos os fallbacks.");
                img.style.opacity = 0.3;
              }}
            />
          ) : null}
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
            }}
          >
            <MapaGraticule codigoImovel={numeroCar} bbox={bboxMunicipio ?? bbox} />
          </div>
        </div>
      </div>

      {/* Direita: legenda HTML de uso do solo */}
      <LegendUsoSoloHtml />
    </div>
  );

  // Linha superior da página 2
  const LinhaTopoDeclividade = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        minHeight: 180,
        gap: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          width: 160 + 70 + 320,
          maxWidth: 560,
        }}
      >
        <MiniMapaSat geom={geomImovel} bbox={bbox} width={160} height={160} />
        <img src={ImgZoom} alt="ZoomImovel" width={70} height={180} />
        <div
          style={{
            width: 320,
            height: 180,
            display: "flex",
            alignItems: "stretch",
            overflow: "hidden",
            borderRadius: 4,
            position: "relative",
          }}
        >
          {objetoPdf?.imagemMunicipio ? (
            <img
              src={objetoPdf.imagemMunicipio}
              alt="Municipio"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                zIndex: 1,
              }}
              onLoad={(e) => {
                const img = e.currentTarget;
                console.warn(
                  "[PdfCartogramaPrint] municipio carregou:",
                  img.src,
                  "size:",
                  img.naturalWidth,
                  "x",
                  img.naturalHeight
                );
              }}
              data-style-fallback="0"
              onError={(e) => {
                const img = e.currentTarget;
                const etapa = img.getAttribute("data-style-fallback") || "0";
                console.warn("[PdfCartogramaPrint] erro img municipio. etapa:", etapa, "src:", img.src);
                if (etapa === "0" && objetoPdf?.imagemMunicipio13Local) {
                  console.warn(
                    "[PdfCartogramaPrint] Fallback municipio: 1.3.0 local workspace",
                    objetoPdf.imagemMunicipio13Local
                  );
                  img.setAttribute("data-style-fallback", "1");
                  img.src = objetoPdf.imagemMunicipio13Local;
                  return;
                }
                if (etapa === "1" && objetoPdf?.imagemMunicipio13LocalSemStyle) {
                  console.warn(
                    "[PdfCartogramaPrint] Fallback municipio: 1.3.0 local ws sem style",
                    objetoPdf.imagemMunicipio13LocalSemStyle
                  );
                  img.setAttribute("data-style-fallback", "2");
                  img.src = objetoPdf.imagemMunicipio13LocalSemStyle;
                  return;
                }
                if (etapa === "2" && objetoPdf?.imagemMunicipio13SemStyle) {
                  console.warn(
                    "[PdfCartogramaPrint] Fallback municipio: 1.3.0 sem style",
                    objetoPdf.imagemMunicipio13SemStyle
                  );
                  img.setAttribute("data-style-fallback", "3");
                  img.src = objetoPdf.imagemMunicipio13SemStyle;
                  return;
                }
                if (etapa === "3" && objetoPdf?.imagemMunicipio13) {
                  console.warn(
                    "[PdfCartogramaPrint] Fallback municipio: 1.3.0 (global)",
                    objetoPdf.imagemMunicipio13
                  );
                  img.setAttribute("data-style-fallback", "4");
                  img.src = objetoPdf.imagemMunicipio13;
                  return;
                }
                if (etapa === "4" && objetoPdf?.imagemMunicipio) {
                  console.warn(
                    "[PdfCartogramaPrint] Fallback municipio: 1.1.0 (lon/lat)",
                    objetoPdf.imagemMunicipio
                  );
                  img.setAttribute("data-style-fallback", "5");
                  img.src = objetoPdf.imagemMunicipio;
                  return;
                }
                if (etapa === "5" && objetoPdf?.imagemMunicipioSemStyle) {
                  console.warn(
                    "[PdfCartogramaPrint] Fallback municipio: 1.1.0 sem style",
                    objetoPdf.imagemMunicipioSemStyle
                  );
                  img.setAttribute("data-style-fallback", "6");
                  img.src = objetoPdf.imagemMunicipioSemStyle;
                  return;
                }
                console.warn("[PdfCartogramaPrint] Mapa municipio falhou em todos os fallbacks.");
                img.style.opacity = 0.3;
              }}
            />
          ) : null}
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
            }}
          >
            <MapaGraticule codigoImovel={numeroCar} bbox={bboxMunicipio ?? bbox} />
          </div>
        </div>
      </div>

      {/* Direita: legenda ESTÁTICA de declividade */}
      <LegendDeclividadeHtml />
    </div>
  );

  return (
    <div
      style={{
        height: "100vh",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <ReactToPrint
        documentTitle="Mapa de Caracterização da Propriedade"
        onBeforePrint={() => dispatch(incrementLoad())}
        onAfterPrint={() => dispatch(decrementLoad())}
        trigger={() => (
          <div
            style={{
              width: "100vw",
              alignItems: "center",
              backgroundColor: "red",
              position: "relative",
              marginBottom: "50px",
              marginTop: "20px",
            }}
          >
            <Button
              variant="contained"
              style={{ position: "absolute", left: "50%" }}
            >
              Mapa de Caracterização da Propriedade
            </Button>
          </div>
        )}
        content={() => componentRef.current}
      />

      <div style={{ backgroundColor: "#ECF0F6" }} ref={componentRef}>
        {/* Página 1 */}
        <FolhaA4>
          <HeaderPdf />
          <PDFContainer>
            <br />
            {LinhaTopoUso}

            {/* Escala gráfica */}
            <EscalaGrafica bbox={bboxEscala} />

            {/* Quadros de usos por ano */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-evenly",
                width: "100%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  marginBottom: "40px",
                }}
              >
                <span>Usos 2008</span>
                <img
                  src={objetoPdf.imagemUsoTerra2008}
                  alt="Usos 2008"
                  width={IMG_W}
                  height={IMG_H}
                  onError={(e) => (e.currentTarget.style.opacity = 0.3)}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <span>Usos 2011</span>
                <img
                  src={objetoPdf.imagemUsoTerra2011}
                  alt="Usos 2011"
                  width={IMG_W}
                  height={IMG_H}
                  onError={(e) => (e.currentTarget.style.opacity = 0.3)}
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-evenly",
                width: "100%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <span>Usos 2016</span>
                <img
                  src={objetoPdf.imagemUsoTerra2016}
                  alt="Usos 2016"
                  width={IMG_W}
                  height={IMG_H}
                  onError={(e) => (e.currentTarget.style.opacity = 0.3)}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <span>Usos 2021</span>
                <img
                  src={objetoPdf.imagemUsoTerra2021}
                  alt="Usos 2021"
                  width={IMG_W}
                  height={IMG_H}
                  onError={(e) => (e.currentTarget.style.opacity = 0.3)}
                />
              </div>
            </div>

            <RodapePdf>
              <ContainerTextoRodapeH>
                <TextoInfo>
                  {
                    "Fonte: Mapeamento de cobertura e uso da terra do Projeto Mapbiomas e Limite do imóvel rural do Cadastro Ambiental Rural - CAR."
                  }
                </TextoInfo>
                <TextoRodapePdf>
                  Embrapa. Sistema de apoio à caracterização ambiental de
                  imóveis rurais.
                </TextoRodapePdf>
                <TextoRodapePdf>
                  http://sacir.cnpma.embrapa.com.br
                </TextoRodapePdf>
              </ContainerTextoRodapeH>
              <TextoRodapePdf>{new Date().toLocaleDateString()}</TextoRodapePdf>
            </RodapePdf>
          </PDFContainer>
        </FolhaA4>

        {/* Página 2 */}
        <FolhaA4>
          <HeaderPdf
            titulo={"Aptidão agrícola, tipo de solo e declividade do imóvel rural"}
          />
          <PDFContainer>
            <SpanTitulo2></SpanTitulo2>

            {LinhaTopoDeclividade}

            <EscalaGrafica bbox={bbox} />

            <div
              style={{
                display: "flex",
                justifyContent: "space-evenly",
                width: "100%",
                marginTop: "50px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <SpanTitulo2>Declividade</SpanTitulo2>
                <img
                  src={objetoPdf.imagemDeclividade}
                  alt="Declividade"
                  width={250}
                  height={250}
                  onError={(e) => (e.currentTarget.style.opacity = 0.3)}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <SpanTitulo2>Aptidão e Solo</SpanTitulo2>
                <img
                  src={objetoPdf.imagemSolo}
                  alt="Aptidão e Solo"
                  width={250}
                  height={250}
                  onError={(e) => (e.currentTarget.style.opacity = 0.3)}
                />
              </div>
            </div>

            <RodapePdf>
              <ContainerTextoRodapeH>
                <TextoInfo>
                  {
                    "Fonte: Mapeamento de cobertura e uso da terra do Projeto Mapbiomas e Limite do imóvel rural do Cadastro Ambiental Rural - CAR."
                  }
                </TextoInfo>
                <TextoRodapePdf>
                  Embrapa. Sistema de apoio à caracterização ambiental de
                  imóveis rurais.
                </TextoRodapePdf>
                <TextoRodapePdf>
                  http://sacir.cnpma.embrapa.com.br
                </TextoRodapePdf>
              </ContainerTextoRodapeH>
              <TextoRodapePdf>{new Date().toLocaleDateString()}</TextoRodapePdf>
            </RodapePdf>
          </PDFContainer>
        </FolhaA4>
      </div>
    </div>
  );
}
